import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useLocationAddress } from '@/hooks/useLocationAddress';
import { 
  Search, 
  Car, 
  MapPin, 
  Clock, 
  MoreVertical,
  Zap,
  ZapOff,
  ChevronRight,
  Plus,
  Minus
} from 'lucide-react';

interface GpsDevice {
  id: string;
  name: string;
  status: 'moving' | 'idle' | 'offline' | 'sleep' | 'unknown';
  lat?: number;
  lon?: number;
  speed_kmh?: number;
  ignition?: boolean;
  battery_level?: number;
  vehicle_plate?: string;
  vehicle_make?: string;
  vehicle_model?: string;
  last_signal_at?: string;
  vehicle?: {
    plate: string;
    make?: string;
    model?: string;
    category?: string;
  } | null;
  driver?: {
    id: string;
    name: string;
    cpf: string;
  } | null;
}

interface VehicleDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  devices: GpsDevice[];
  selectedDeviceId?: string;
  onDeviceSelect: (deviceId: string) => void;
  companyId?: string;
}

export function VehicleDrawer({
  open,
  onOpenChange,
  devices,
  selectedDeviceId,
  onDeviceSelect,
  companyId
}: VehicleDrawerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isMinimized, setIsMinimized] = useState(false);

  // Filtrar apenas por busca
  const filteredDevices = devices.filter(device => {
    return device.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      device.vehicle?.plate?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const onlineDevices = filteredDevices.filter(d => 
    d.status === 'moving' || d.status === 'idle'
  );
  
  const offlineDevices = filteredDevices.filter(d => 
    d.status === 'offline' || d.status === 'sleep' || d.status === 'unknown'
  );

  return (
    <>
      {/* Painel flutuante fixo no canto superior direito */}
      <div className={`
        fixed top-20 right-4 
        w-[320px] max-w-[calc(100vw-2rem)]
        bg-background/95 backdrop-blur-md
        rounded-xl shadow-2xl border border-border/50
        z-50 flex flex-col overflow-hidden
        transition-all duration-300 ease-in-out
        ${open ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-[360px] pointer-events-none'}
        ${isMinimized ? 'max-h-[60px]' : 'max-h-[calc(100vh-6rem)]'}
      `}>
        {/* Header fixo */}
        <div className={`
          px-4 py-3 space-y-3 rounded-t-xl
          bg-[#0f1729] text-white
          ${!isMinimized && 'border-b border-white/10'}
        `}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Car className="h-5 w-5 text-white" />
              <h2 className="text-base font-semibold">Status dos veículos</h2>
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30">
                {devices.length}
              </Badge>
            </div>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMinimized(!isMinimized)}
              className="h-8 w-8 hover:bg-white/10 text-white"
              title={isMinimized ? "Expandir painel" : "Minimizar painel"}
            >
              {isMinimized ? (
                <Plus className="h-5 w-5" />
              ) : (
                <Minus className="h-5 w-5" />
              )}
            </Button>
          </div>

          {/* Busca - esconder quando minimizado */}
          {!isMinimized && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Pesquise por um veículo"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
              />
            </div>
          )}
        </div>

        {/* Lista scrollável - esconder quando minimizado */}
        {!isMinimized && (
          <ScrollArea className="flex-1 px-3">
            {filteredDevices.length > 0 && (
              <div className="py-3 space-y-1.5">
                {filteredDevices.map(device => (
                  <VehicleCard
                    key={device.id}
                    device={device}
                    isSelected={selectedDeviceId === device.id}
                    onClick={() => onDeviceSelect(device.id)}
                    companyId={companyId}
                  />
                ))}
              </div>
            )}

            {filteredDevices.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Car className="h-12 w-12 mb-4 opacity-20" />
                <p>Nenhum veículo encontrado</p>
              </div>
            )}
          </ScrollArea>
        )}
      </div>
    </>
  );
}

// Componente auxiliar para carregar imagem do veículo
function VehicleImage({ 
  make, 
  model 
}: { 
  make?: string; 
  model?: string;
}) {
  const [imageError, setImageError] = useState(false);
  
  // Gerar gradiente baseado na marca
  const getBrandGradient = (brand?: string) => {
    if (!brand) return 'from-primary/20 to-primary/5';
    
    const brandColors: Record<string, string> = {
      'toyota': 'from-red-500/20 to-red-600/5',
      'ford': 'from-blue-500/20 to-blue-600/5',
      'chevrolet': 'from-yellow-500/20 to-yellow-600/5',
      'volkswagen': 'from-blue-400/20 to-blue-500/5',
      'fiat': 'from-red-400/20 to-red-500/5',
      'honda': 'from-red-600/20 to-red-700/5',
      'hyundai': 'from-blue-600/20 to-blue-700/5',
      'renault': 'from-yellow-400/20 to-yellow-500/5',
      'nissan': 'from-gray-500/20 to-gray-600/5',
      'mercedes': 'from-slate-500/20 to-slate-600/5',
      'bmw': 'from-blue-700/20 to-blue-800/5',
      'audi': 'from-gray-600/20 to-gray-700/5',
    };
    
    return brandColors[brand.toLowerCase()] || 'from-primary/20 to-primary/5';
  };
  
  const getVehicleImageUrl = () => {
    if (!make || !model) return null;
    // Query melhorada: marca + modelo + ano recente
    const query = encodeURIComponent(`${make} ${model} 2020 car`);
    return `https://source.unsplash.com/200x200/?${query}`;
  };
  
  const imageUrl = getVehicleImageUrl();
  
  // Gerar iniciais da marca para fallback
  const getBrandInitials = (brand?: string) => {
    if (!brand) return 'CAR';
    return brand.substring(0, 3).toUpperCase();
  };
  
  if (!imageUrl || imageError) {
    return (
      <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${getBrandGradient(make)} flex items-center justify-center flex-shrink-0 border border-border/50`}>
        {make ? (
          <span className="text-[10px] font-bold text-foreground/70">
            {getBrandInitials(make)}
          </span>
        ) : (
          <Car className="h-6 w-6 text-primary" />
        )}
      </div>
    );
  }
  
  return (
    <img
      src={imageUrl}
      alt={`${make} ${model}`}
      className="w-12 h-12 rounded-lg object-cover flex-shrink-0 border border-border/20"
      onError={() => setImageError(true)}
    />
  );
}

// Componente auxiliar para exibir endereço via geocoding reverso
// Componente para exibir informações do motorista
function DriverInfo({ 
  driver 
}: { 
  driver?: { id: string; name: string; cpf: string } | null;
}) {
  if (!driver) {
    return (
      <div className="flex items-center gap-2 text-xs">
        <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
          <span className="text-[10px] font-semibold text-muted-foreground">?</span>
        </div>
        <div className="flex-1">
          <p className="font-medium text-muted-foreground">Sem motorista</p>
          <p className="text-[10px] text-destructive">Sem identificação</p>
        </div>
      </div>
    );
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .slice(0, 2)
      .map(n => n[0])
      .join('')
      .toUpperCase();
  };

  return (
    <div className="flex items-center gap-2 text-xs">
      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 border border-primary/20">
        <span className="text-[10px] font-semibold text-primary">
          {getInitials(driver.name)}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-foreground truncate">{driver.name}</p>
        <p className="text-[10px] text-muted-foreground font-mono">{driver.cpf}</p>
      </div>
    </div>
  );
}

function VehicleAddress({
  latitude, 
  longitude, 
  companyId 
}: { 
  latitude?: number; 
  longitude?: number; 
  companyId?: string;
}) {
  const { data: address, isLoading } = useLocationAddress(
    companyId,
    latitude,
    longitude
  );

  if (!latitude || !longitude) return null;

  return (
    <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
      <MapPin className="h-3 w-3 mt-0.5 flex-shrink-0" />
      <span className="line-clamp-2">
        {isLoading ? (
          <span className="animate-pulse">Carregando endereço...</span>
        ) : address?.formatted ? (
          address.formatted
        ) : (
          `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`
        )}
      </span>
    </div>
  );
}

// Card individual de veículo
function VehicleCard({ 
  device, 
  isSelected, 
  onClick,
  companyId
}: { 
  device: GpsDevice; 
  isSelected: boolean; 
  onClick: () => void;
  companyId?: string;
}) {
  const getStatusIndicator = (device: GpsDevice) => {
    if (device.ignition && device.speed_kmh && device.speed_kmh > 5) {
      return { color: 'bg-green-500', text: 'Em movimento' };
    }
    if (device.ignition && (!device.speed_kmh || device.speed_kmh <= 5)) {
      return { color: 'bg-orange-500', text: 'Parado (ligado)' };
    }
    if (!device.ignition || device.status === 'offline') {
      return { color: 'bg-red-500', text: 'Desligado' };
    }
    return { color: 'bg-gray-400', text: 'Sem sinal' };
  };

  const statusIndicator = getStatusIndicator(device);

  const vehicleTitle = device.vehicle?.model 
    ? `${device.vehicle.make || ''} ${device.vehicle.model}`.trim()
    : device.name;

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <div
      onClick={onClick}
      className={`
        flex flex-col gap-2 p-3 rounded-lg border cursor-pointer 
        transition-all hover:shadow-md hover:border-primary/50
        ${isSelected ? 'border-primary bg-primary/5 shadow-sm' : 'border-border bg-card'}
      `}
    >
      {/* Cabeçalho: Imagem + Título + Menu */}
      <div className="flex items-start gap-2.5">
        <VehicleImage 
          make={device.vehicle?.make}
          model={device.vehicle?.model}
        />

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-sm truncate">
                {vehicleTitle}
              </h4>
              {device.vehicle?.plate && (
                <p className="text-xs text-muted-foreground font-mono">
                  {device.vehicle.plate}
                </p>
              )}
            </div>
            
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-6 w-6 flex-shrink-0"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Timestamp */}
      {device.last_signal_at && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Clock className="h-3 w-3 flex-shrink-0" />
          <span>Atualizado em: {formatTimestamp(device.last_signal_at)}</span>
        </div>
      )}

      {/* Endereço via geocoding reverso */}
      <VehicleAddress 
        latitude={device.lat}
        longitude={device.lon}
        companyId={companyId}
      />

      {/* Informações do motorista */}
      <DriverInfo driver={device.driver} />

      {/* Rodapé: Status + Velocidade + Bateria */}
      <div className="flex items-center gap-2 flex-wrap pt-1 border-t">
        <div className="flex items-center gap-1.5">
          <div className={`h-2 w-2 rounded-full ${statusIndicator.color} shadow-sm`} />
          <span className="text-xs font-medium text-muted-foreground">
            {statusIndicator.text}
          </span>
        </div>
        
        {device.speed_kmh !== undefined && device.speed_kmh > 0 && (
          <Badge variant="outline" className="text-xs h-5 px-1.5">
            {device.speed_kmh} km/h
          </Badge>
        )}
        
        {device.battery_level !== undefined && (
          <Badge variant="outline" className="text-xs h-5 px-1.5">
            🔋 {device.battery_level}%
          </Badge>
        )}
      </div>
    </div>
  );
}

